import {CorrectWrong , QuestionHistories } from '@/models/questionHistory'
import {BREAK_DATA_VERSION} from '@/consts/break_data_version'
import { localStorageService } from '@/util/local_storage_service'

class BreakData {
    question_set_id: number
    data_created_at: string
    data_aborted_at: string
    data_updated_at: string
    app_version: string
    correct_wrong_list: CorrectWrong[]
    saved_answers: QuestionHistories[]
    interrupt_index: number
    interrupt_path: string
    study_target?: number;

    /**
     * BreakDataのコンストラクタ
     * @param question_set_id - 出題単位のID
     * @param data_created_at - データ作成日時
     * @param data_aborted_at - データ中断日時
     * @param data_updated_at - データ更新日時
     * @param app_version - アプリのバージョン
     * @param correct_wrong_list - 正誤リスト
     * @param saved_answers - 保存された解答
     * @param interrupt_index - 中断した小問インデックス
     * @param interrupt_path - 中断したパス（questionかanswerかjudgeか）
     * @param study_target - 学習対象（オプション）
     */
    constructor(
        question_set_id: number,
        data_created_at: string,
        data_aborted_at: string | null,
        data_updated_at: string | null,
        app_version: string,
        correct_wrong_list: CorrectWrong[],
        saved_answers: QuestionHistories[],
        interrupt_index: number,
        interrupt_path: string,
        study_target?: number 
    ) {
        this.question_set_id = question_set_id;
        this.data_created_at = data_created_at;
        this.data_aborted_at = data_aborted_at;
        this.data_updated_at = data_updated_at;
        this.app_version = app_version;
        this.correct_wrong_list = correct_wrong_list;
        this.saved_answers = saved_answers;
        this.interrupt_index = interrupt_index;
        this.interrupt_path = interrupt_path;
        this.study_target = study_target;
    }

    /**
     * データからBreakDataインスタンスを生成。
     * @param data - 入力データ
     * @param schemaVersion - スキーマバージョン（デフォルトは1）
     * @returns BreakDataインスタンス
     */
    static fromData(data: any, schemaVersion: number = 1): BreakData {
        switch (schemaVersion) {
            case BREAK_DATA_VERSION.VERSION2:
                return this.fromDataV2(data);
            case BREAK_DATA_VERSION.VERSION1:
            default:
                return this.fromDataV1(data);
        }
    }

    /**
     * バージョン1のデータからBreakDataインスタンスを生成。
     * @param data - 入力データ
     * @returns BreakDataインスタンス
     */
    static fromDataV1(data: any): BreakData {
        return new BreakData(
            data.question_set_id,
            data.data_created_at,
            data.data_aborted_at || null,
            data.data_updated_at || null,
            data.app_version,
            data.correct_wrong_list.map((cw_item: any) => CorrectWrong.fromData(cw_item)),
            data.saved_answers.map((save_answer_item: any) => QuestionHistories.fromData(save_answer_item)),
            data.interrupt_index || 0,
            data.interrupt_path || 'question'
        );
    }

    /**
     * バージョン2のデータからBreakDataインスタンスを生成。
     * @param data - 入力データ
     * @returns BreakDataインスタンス
     */
    static fromDataV2(data: any): BreakData {
        const breakData  = this.fromDataV1(data); // V1の処理を継続
        breakData.study_target = data.study_target;
        return breakData ;
    }
}

class WorkBreakData {
    break_data: BreakData | null
    temporary_data: BreakData | null
    break_data_version: number;

    /**
     * WorkBreakDataのコンストラクタ
     * @param break_data - 正式中断データ
     * @param temporary_data - 一時的中断データ
     * @param break_data_version - 中断データのバージョン
     */
    constructor(break_data: BreakData | null, temporary_data: BreakData | null, break_data_version: number) {
        this.break_data = break_data
        this.temporary_data = temporary_data
        this.break_data_version = break_data_version;
    }

    /**
     * データからWorkBreakDataインスタンスを生成。
     * @param data - 入力データ
     * @returns WorkBreakDataインスタンス
     */
    static fromData(data: any): WorkBreakData {
        const break_data_version = data.break_data_version !== undefined ? data.break_data_version : BREAK_DATA_VERSION.VERSION1;
        const break_data = data.break_data === null ? null : BreakData.fromData(data.break_data, break_data_version)
        const temporary_data = data.temporary_data === null ? null : BreakData.fromData(data.temporary_data, break_data_version)
        return new WorkBreakData(break_data, temporary_data, break_data_version)
    }
}

class BreakDataCollection {
    [key: string]: WorkBreakData;

    /**
     * BreakDataCollectionのコンストラクタ
     * @param workDatas - WorkBreakDataのオブジェクト
     */
    constructor(workDatas: { [key: string]: WorkBreakData }) {
        Object.assign(this, workDatas);
    }

    /**
     * データからBreakDataCollectionインスタンスを生成。
     * @param data - 入力データ
     * @returns BreakDataCollectionインスタンス
     */
    static fromData(data: any): BreakDataCollection {
        const workDatas: { [key: string]: WorkBreakData } = {};
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                workDatas[key] = WorkBreakData.fromData(data[key]);
            }
        }
        return new BreakDataCollection(workDatas);
    }

    /**
     * ローカルストレージからデータを読み込み、BreakDataCollectionを生成。
     * @returns BreakDataCollectionインスタンス
     */
    static load(): BreakDataCollection {
        const study_break_data = localStorageService.loadStudyBreakData();
        return this.fromData(study_break_data);
    }
}

export {BreakDataCollection, WorkBreakData}